import React, { useState, useEffect } from 'react';
import { User, Briefcase, GraduationCap, Globe, Monitor, Car, Target, X, Save, Loader2, Trash2, ChevronLeft, PlusCircle } from 'lucide-react';
import { LITHUANIAN_CITIES } from '../../constants/cities';
import FilterModal from '../Jobs/FilterModal';
import api from '../../services/api';
import axios from 'axios';
import './CandidateSearchFilters.scss';

interface CVTemplate {
  id: number;
  category: string;
  value: string;
  parent_value?: string | null;
}

interface FilterOptions {
  searchKeyword: string;
  keywords: string[]; // Keyword chips
  location: string[];
  experience: string[];
  education: string[];
  languages: string[];
  computerSkills: string[];
  drivingLicense: string[];
  salary: string;
  workType: string[];
  age: string;
  gender: string[];
  status: string[];
  activity: string;
  cvLanguage: string[];
  hasPhoto: boolean;
  hasRecommendation: boolean;
  // Company-related filters
  companyNames: string[];
  lastWorkField: string[];
  companyActivity: string[];
}

interface CandidateSearchFiltersProps {
  isVisible: boolean;
  onFiltersChange: (filters: FilterOptions) => void;
  onFiltersPreview?: (filters: FilterOptions) => void;
  onCollapse?: () => void;
  currentFilters: FilterOptions;
  liveCount?: number | null;
  countLoading?: boolean;
}

const CandidateSearchFilters: React.FC<CandidateSearchFiltersProps> = ({
  isVisible,
  onFiltersChange,
  onFiltersPreview,
  onCollapse,
  currentFilters,
  liveCount,
  countLoading
}) => {
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [workFieldSearch, setWorkFieldSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [searchTitle, setSearchTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Experience modal tab state
  const [experienceTab, setExperienceTab] = useState<'workField' | 'lastWorkField' | 'companies'>('workField');
  
  // Company data from backend
  const [companies, setCompanies] = useState<{ companyName: string; count: number; activitySector?: string }[]>([]);
  const [workFieldCounts, setWorkFieldCounts] = useState<{ workField: string; count: number }[]>([]);
  const [lastWorkFieldCounts, setLastWorkFieldCounts] = useState<{ workField: string; count: number }[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Local state for modal filters - prevents re-renders during interaction
  const [localFilters, setLocalFilters] = useState<FilterOptions>(currentFilters);
  const [localAgeMin, setLocalAgeMin] = useState<number>(16);
  const [localAgeMax, setLocalAgeMax] = useState<number>(65);
  
  // CV Templates from admin
  const [cvTemplates, setCvTemplates] = useState<{
    education_level?: CVTemplate[];
    language?: CVTemplate[];
    cv_language?: CVTemplate[];
    work_field?: CVTemplate[];
    position?: CVTemplate[];
    position_type?: CVTemplate[];
    work_type?: CVTemplate[];
    driving_category?: CVTemplate[];
    computer_skill_office?: CVTemplate[];
    computer_skill_accounting?: CVTemplate[];
    computer_skill_os?: CVTemplate[];
    computer_skill_programming?: CVTemplate[];
    computer_skill_database?: CVTemplate[];
    city?: CVTemplate[];
  }>({});

  // Load CV templates on mount
  useEffect(() => {
    loadCvTemplates();
  }, []);

  // Sync local age state with currentFilters when filters change externally
  useEffect(() => {
    if (currentFilters.age) {
      const [min, max] = currentFilters.age.split('-').map(Number);
      if (!isNaN(min)) setLocalAgeMin(min);
      if (!isNaN(max)) setLocalAgeMax(max);
    } else {
      setLocalAgeMin(16);
      setLocalAgeMax(65);
    }
  }, [currentFilters.age]);

  const loadCvTemplates = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/cv-templates?grouped=true`);
      if (response.data && response.data.data) {
        setCvTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error loading CV templates:', error);
    }
  };

  // Load company data for the experience filter
  const loadCompanyData = async () => {
    if (companies.length > 0) return; // Already loaded
    
    setLoadingCompanies(true);
    try {
      const [companiesRes, workFieldsRes, lastWorkFieldsRes] = await Promise.all([
        // Use approved companies endpoint (admin-approved only)
        axios.get(`${process.env.REACT_APP_API_URL}/companies/approved`),
        axios.get(`${process.env.REACT_APP_API_URL}/cv/public/work-fields`),
        axios.get(`${process.env.REACT_APP_API_URL}/cv/public/last-work-fields`)
      ]);
      
      if (companiesRes.data?.data) setCompanies(companiesRes.data.data);
      if (workFieldsRes.data?.data) setWorkFieldCounts(workFieldsRes.data.data);
      if (lastWorkFieldsRes.data?.data) setLastWorkFieldCounts(lastWorkFieldsRes.data.data);
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const openFilterModal = (modalName: string) => {
    // Sync local filters with current filters when opening modal
    setLocalFilters(currentFilters);
    // Sync age slider
    if (currentFilters.age) {
      const [min, max] = currentFilters.age.split('-').map(Number);
      if (!isNaN(min)) setLocalAgeMin(min);
      if (!isNaN(max)) setLocalAgeMax(max);
    } else {
      setLocalAgeMin(16);
      setLocalAgeMax(65);
    }
    
    // Load company data when opening experience modal
    if (modalName === 'experience') {
      loadCompanyData();
      setExperienceTab('workField'); // Reset to first tab
    }
    
    setOpenModal(modalName);
  };

  const closeModal = () => {
    // Apply all local filter changes to parent when closing modal
    // Only set age if it's different from the default range (16-65)
    const isAgeChanged = localAgeMin !== 16 || localAgeMax !== 65;
    const finalFilters = {
      ...localFilters,
      age: isAgeChanged ? `${localAgeMin}-${localAgeMax}` : ''
    };
    onFiltersChange(finalFilters);
    setOpenModal(null);
  };

  // Trigger preview count when local filters change in modal
  const triggerPreview = (newLocalFilters: FilterOptions, ageMin: number = localAgeMin, ageMax: number = localAgeMax) => {
    if (onFiltersPreview) {
      // Only include age if it's different from default range
      const isAgeChanged = ageMin !== 16 || ageMax !== 65;
      const previewFilters = {
        ...newLocalFilters,
        age: isAgeChanged ? `${ageMin}-${ageMax}` : ''
      };
      onFiltersPreview(previewFilters);
    }
  };

  // Local handlers for modal - update local state and trigger preview
  const handleLocalArrayCheckbox = (field: keyof FilterOptions, value: string) => {
    const currentValues = localFilters[field] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    const newFilters = {
      ...localFilters,
      [field]: newValues
    };
    setLocalFilters(newFilters);
    triggerPreview(newFilters);
  };

  const handleLocalInputChange = (field: keyof FilterOptions, value: string) => {
    const newFilters = {
      ...localFilters,
      [field]: value
    };
    setLocalFilters(newFilters);
    triggerPreview(newFilters);
  };

  const handleLocalBooleanChange = (field: keyof FilterOptions, value: boolean) => {
    const newFilters = {
      ...localFilters,
      [field]: value
    };
    setLocalFilters(newFilters);
    triggerPreview(newFilters);
  };

  // Original handlers for non-modal interactions (immediate parent update)
  const handleArrayCheckbox = (field: keyof FilterOptions, value: string) => {
    const currentValues = currentFilters[field] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    onFiltersChange({
      ...currentFilters,
      [field]: newValues
    });
  };

  const handleInputChange = (field: keyof FilterOptions, value: string) => {
    onFiltersChange({
      ...currentFilters,
      [field]: value
    });
  };

  const handleBooleanChange = (field: keyof FilterOptions, value: boolean) => {
    onFiltersChange({
      ...currentFilters,
      [field]: value
    });
  };

  const clearAllFilters = () => {
    // Clear localStorage when clearing all filters
    localStorage.removeItem('skelbimai_filters');
    
    onFiltersChange({
      searchKeyword: '',
      keywords: [],
      location: [],
      experience: [],
      education: [],
      languages: [],
      computerSkills: [],
      drivingLicense: [],
      salary: '',
      workType: [],
      age: '',
      gender: [],
      status: [],
      activity: '',
      cvLanguage: [],
      hasPhoto: false,
      hasRecommendation: false,
      companyNames: [],
      lastWorkField: [],
      companyActivity: []
    });
  };

  const handleSaveSearch = () => {
    setShowSaveModal(true);
    setSaveMessage(null);
  };

  const confirmSaveSearch = async () => {
    if (!searchTitle.trim()) {
      setSaveMessage({ type: 'error', text: 'Prašome įvesti paieškos pavadinimą' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Combine searchKeyword and keywords chips
      const allKeywords = [...currentFilters.keywords];
      if (currentFilters.searchKeyword && currentFilters.searchKeyword.trim()) {
        allKeywords.push(currentFilters.searchKeyword.trim());
      }

      // Prepare search data matching backend model
      const searchData = {
        title: searchTitle.trim(),
        location: currentFilters.location.length > 0 ? currentFilters.location : undefined,
        positions: allKeywords.length > 0 ? allKeywords : undefined,
        education_level: currentFilters.education.length > 0 ? currentFilters.education : undefined,
        languages: currentFilters.languages.length > 0 ? currentFilters.languages : undefined,
        technical_skills: currentFilters.computerSkills.length > 0 ? currentFilters.computerSkills : undefined,
        desired_work_type: currentFilters.workType.length > 0 ? currentFilters.workType : undefined,
        desired_salary: currentFilters.salary || undefined,
        experience_years: currentFilters.experience.length > 0 ? currentFilters.experience.join(', ') : undefined,
        is_subscribed: true // Set to true so it appears in "Išsaugotos paieškos" tab
      };

      const response = await api.post('/employer-saved-searches', searchData);

      if (response.data.success) {
        setSaveMessage({ type: 'success', text: 'Paieška sėkmingai išsaugota!' });
        setTimeout(() => {
          setShowSaveModal(false);
          setSearchTitle('');
          setSaveMessage(null);
        }, 1500);
      } else {
        setSaveMessage({ type: 'error', text: 'Nepavyko išsaugoti paieškos' });
      }
    } catch (error: any) {
      console.error('Save search error:', error);
      setSaveMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Klaida išsaugant paiešką' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const cancelSaveSearch = () => {
    setShowSaveModal(false);
    setSearchTitle('');
    setSaveMessage(null);
  };

  const removeFilter = (field: keyof FilterOptions, value?: string) => {
    if (field === 'hasPhoto' || field === 'hasRecommendation') {
      handleBooleanChange(field, false);
    } else if (value && Array.isArray(currentFilters[field])) {
      handleArrayCheckbox(field, value);
    } else {
      handleInputChange(field, '');
    }
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active: { field: keyof FilterOptions; value: string; label: string }[] = [];
    
    // Add keywords as chips
    currentFilters.keywords.forEach(keyword => {
      active.push({ field: 'keywords', value: keyword, label: keyword });
    });
    
    currentFilters.location.forEach(loc => {
      active.push({ field: 'location', value: loc, label: loc });
    });
    
    currentFilters.experience.forEach(exp => {
      active.push({ field: 'experience', value: exp, label: exp });
    });
    
    currentFilters.education.forEach(edu => {
      active.push({ field: 'education', value: edu, label: edu });
    });
    
    currentFilters.languages.forEach(lang => {
      active.push({ field: 'languages', value: lang, label: lang });
    });
    
    currentFilters.computerSkills.forEach(skill => {
      active.push({ field: 'computerSkills', value: skill, label: skill });
    });
    
    currentFilters.drivingLicense.forEach(license => {
      active.push({ field: 'drivingLicense', value: license, label: `Vairavimas: ${license}` });
    });
    
    currentFilters.workType.forEach(type => {
      active.push({ field: 'workType', value: type, label: type });
    });
    
    // Only show age as active filter if it's not the default range (16-65)
    if (currentFilters.age && currentFilters.age !== '16-65') {
      active.push({ field: 'age', value: currentFilters.age, label: `Amžius: ${currentFilters.age}` });
    }
    
    if (currentFilters.salary) {
      active.push({ field: 'salary', value: currentFilters.salary, label: `Atlyginimas: ${currentFilters.salary}` });
    }

    // New personal data filters
    currentFilters.gender?.forEach(g => {
      active.push({ field: 'gender', value: g, label: g });
    });

    currentFilters.status?.forEach(s => {
      active.push({ field: 'status', value: s, label: s === 'currently_employed' ? 'Šiuo metu dirbantis' : s });
    });

    if (currentFilters.activity) {
      active.push({ field: 'activity', value: currentFilters.activity, label: `Aktyvumas: ${currentFilters.activity}` });
    }

    currentFilters.cvLanguage?.forEach(lang => {
      active.push({ field: 'cvLanguage', value: lang, label: `CV: ${lang}` });
    });

    if (currentFilters.hasPhoto) {
      active.push({ field: 'hasPhoto', value: 'true', label: 'CV su nuotrauka' });
    }

    if (currentFilters.hasRecommendation) {
      active.push({ field: 'hasRecommendation', value: 'true', label: 'CV su rekomendacija' });
    }

    // Company-related filters
    currentFilters.companyNames?.forEach(company => {
      active.push({ field: 'companyNames', value: company, label: `Įmonė: ${company}` });
    });

    currentFilters.lastWorkField?.forEach(field => {
      active.push({ field: 'lastWorkField', value: field, label: `Pask. darbas: ${field}` });
    });

    currentFilters.companyActivity?.forEach(activity => {
      active.push({ field: 'companyActivity', value: activity, label: `Veikla: ${activity}` });
    });
    
    return active;
  };

  const activeFilters = getActiveFilters();

  if (!isVisible) return null;

  return (
    <div className="candidate-search-filters">

      {/* Selected Filters - Grouped by section */}
      {activeFilters.length > 0 && (
        <div className="selected-filters-section">
          <div className="section-header">
            <h3>PASIRINKTI KRITERIJAI</h3>
            <button className="clear-all-btn" onClick={clearAllFilters}>
              <X size={14} />
            </button>
          </div>
          <div className="selected-filters">
            {(() => {
              const fieldToModal: Record<string, string> = {
                location: 'personalData', gender: 'personalData', status: 'personalData',
                activity: 'personalData', cvLanguage: 'personalData', age: 'personalData',
                experience: 'experience', lastWorkField: 'experience',
                companyNames: 'experience', companyActivity: 'experience',
                education: 'education', languages: 'languages',
                computerSkills: 'computerSkills', drivingLicense: 'drivingSkills',
                workType: 'requirements', salary: 'requirements',
                keywords: 'personalData',
              };
              const groups: Record<string, typeof activeFilters> = {};
              activeFilters.forEach(f => {
                const modal = fieldToModal[f.field] || 'personalData';
                if (!groups[modal]) groups[modal] = [];
                groups[modal].push(f);
              });
              return Object.entries(groups).map(([modal, filters]) => (
                <div key={modal} className="filter-group">
                  <div className="filter-group-pills">
                    {filters.map((filter, index) => (
                      <div key={index} className="filter-tag">
                        <span>{filter.label}</span>
                        <button onClick={() => removeFilter(filter.field, filter.value)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="filter-group-add" onClick={() => openFilterModal(modal)} title="Pridėti filtrą">
                    <PlusCircle size={22} />
                  </button>
                </div>
              ));
            })()}
          </div>
          
          {/* Save Search Button */}
          <button className="save-search-btn" onClick={handleSaveSearch}>
            <Save size={16} />
            <span>Išsaugoti paiešką</span>
          </button>
        </div>
      )}

      <div className="filters-content">
        <div className="filters-header">
          <h3>PASIRINKTI KRITERIJAI</h3>
          <div className="filters-header-actions">
            <button className="clear-filters-icon" onClick={clearAllFilters} title="Išvalyti filtrus">
              <Trash2 size={18} />
            </button>
            {onCollapse && (
              <button className="collapse-filters-btn" onClick={onCollapse} title="Slėpti filtrus">
                <ChevronLeft size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Personal Data */}
        {(() => {
          const count = (currentFilters.location?.length || 0) + (currentFilters.gender?.length || 0) +
            (currentFilters.status?.length || 0) + (currentFilters.activity ? 1 : 0) +
            (currentFilters.cvLanguage?.length || 0) + (currentFilters.hasPhoto ? 1 : 0) +
            (currentFilters.hasRecommendation ? 1 : 0) + (currentFilters.age ? 1 : 0);
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('personalData')}>
                <div className="header-left">
                  <User size={18} />
                  <span>ASMENINIAI DUOMENYS{count > 0 && <span className="filter-count"> ({count})</span>}</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Work Experience */}
        {(() => {
          const count = (currentFilters.experience?.length || 0) + (currentFilters.lastWorkField?.length || 0) +
            (currentFilters.companyNames?.length || 0) + (currentFilters.companyActivity?.length || 0);
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('experience')}>
                <div className="header-left">
                  <Briefcase size={18} />
                  <span>DARBO PATIRTIS{count > 0 && <span className="filter-count"> ({count})</span>}</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Education */}
        {(() => {
          const count = currentFilters.education?.length || 0;
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('education')}>
                <div className="header-left">
                  <GraduationCap size={18} />
                  <span>IŠSILAVINIMAS{count > 0 && <span className="filter-count"> ({count})</span>}</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Language Skills */}
        {(() => {
          const count = currentFilters.languages?.length || 0;
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('languages')}>
                <div className="header-left">
                  <Globe size={18} />
                  <span>KALBŲ ĮGŪDŽIAI{count > 0 && <span className="filter-count"> ({count})</span>}</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Computer Skills */}
        {(() => {
          const count = currentFilters.computerSkills?.length || 0;
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('computerSkills')}>
                <div className="header-left">
                  <Monitor size={18} />
                  <span>DARBO KOMPIUTERIU ĮGŪDŽIAI{count > 0 && <span className="filter-count"> ({count})</span>}</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Driving Skills */}
        {(() => {
          const count = currentFilters.drivingLicense?.length || 0;
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('drivingSkills')}>
                <div className="header-left">
                  <Car size={18} />
                  <span>VAIRAVIMO ĮGŪDŽIAI{count > 0 && <span className="filter-count"> ({count})</span>}</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Requirements for Future Job */}
        {(() => {
          const count = (currentFilters.workType?.length || 0) + (currentFilters.salary ? 1 : 0);
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('requirements')}>
                <div className="header-left">
                  <Target size={18} />
                  <span>REIKALAVIMAI BŪSIMAM DARBUI{count > 0 && <span className="filter-count"> ({count})</span>}</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}
      </div>

      {/* Filter Modals */}
      {openModal === 'personalData' && (
        <FilterModal
          title="Asmeniniai duomenys"
          icon={<User size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          countLabel="candidates"
        >
          <div className="two-column-grid">
            {/* Left Column - Personal filters */}
            <div className="filter-column">
              {/* Amžius */}
              <div className="modal-filter-group">
                <h4>Amžius</h4>
                <div className="age-slider-container">
                  <div className="age-slider-track">
                    <div 
                      className="age-slider-progress"
                      style={{
                        left: `${((localAgeMin - 16) / 49) * 100}%`,
                        right: `${100 - ((localAgeMax - 16) / 49) * 100}%`
                      }}
                    />
                    <input
                      type="range"
                      min="16"
                      max="65"
                      value={localAgeMin}
                      onChange={(e) => {
                        const newMin = parseInt(e.target.value);
                        if (newMin < localAgeMax) {
                          setLocalAgeMin(newMin);
                        }
                      }}
                      className="age-slider age-slider-min"
                    />
                    <input
                      type="range"
                      min="16"
                      max="65"
                      value={localAgeMax}
                      onChange={(e) => {
                        const newMax = parseInt(e.target.value);
                        if (newMax > localAgeMin) {
                          setLocalAgeMax(newMax);
                        }
                      }}
                      className="age-slider age-slider-max"
                    />
                  </div>
                  <div className="age-values">
                    <span>{localAgeMin}</span>
                    <span>{localAgeMax}</span>
                  </div>
                </div>
              </div>

              {/* Lytis */}
              <div className="modal-filter-group">
                <h4>Lytis</h4>
                <div className="checkbox-row">
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.gender?.includes('Vyras') || false}
                      onChange={() => handleLocalArrayCheckbox('gender', 'Vyras')}
                    />
                    <span>Vyras</span>
                  </label>
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.gender?.includes('Moteris') || false}
                      onChange={() => handleLocalArrayCheckbox('gender', 'Moteris')}
                    />
                    <span>Moteris</span>
                  </label>
                </div>
              </div>

              {/* Statusas */}
              <div className="modal-filter-group">
                <h4>Statusas</h4>
                <div className="checkbox-list single-column">
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.status?.includes('currently_employed') || false}
                      onChange={() => handleLocalArrayCheckbox('status', 'currently_employed')}
                    />
                    <span>Šiuo metu dirbantis</span>
                  </label>
                </div>
              </div>

              {/* Aktyvumas */}
              <div className="modal-filter-group">
                <h4>Aktyvumas</h4>
                <p className="filter-subtitle">(Paskutinio aktyvumo svetainėje laikas)</p>
                <div className="checkbox-list single-column">
                  {['Šiandien', 'Per paskutines 3 dienas', 'Per paskutinę savaitę', 'Per paskutines 2 savaites', 'Per paskutines 4 savaites'].map((activity) => (
                    <label key={activity} className="checkbox-item">
                      <input
                        type="radio"
                        name="activity"
                        checked={localFilters.activity === activity}
                        onChange={() => handleLocalInputChange('activity', activity)}
                      />
                      <span>{activity}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* CV kalba */}
              <div className="modal-filter-group">
                <h4>CV kalba</h4>
                <div className="checkbox-list single-column">
                  {(cvTemplates.cv_language || []).map((lang) => (
                    <label key={lang.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={localFilters.cvLanguage?.includes(lang.value) || false}
                        onChange={() => handleLocalArrayCheckbox('cvLanguage', lang.value)}
                      />
                      <span>{lang.value}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Nuotrauka & Rekomendacija */}
              <div className="modal-filter-group">
                <h4>Nuotrauka</h4>
                <div className="checkbox-list single-column">
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.hasPhoto || false}
                      onChange={() => handleLocalBooleanChange('hasPhoto', !localFilters.hasPhoto)}
                    />
                    <span>CV su nuotrauka</span>
                  </label>
                </div>
              </div>

              <div className="modal-filter-group">
                <h4>Rekomendacija</h4>
                <div className="checkbox-list single-column">
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.hasRecommendation || false}
                      onChange={() => handleLocalBooleanChange('hasRecommendation', !localFilters.hasRecommendation)}
                    />
                    <span>CV su rekomendacija</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Right Column - Gyvenamoji vieta */}
            <div className="filter-column">
              <h4>Gyvenamoji vieta</h4>
              <input
                type="text"
                placeholder="Įveskite miestą"
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                className="filter-search-input"
              />
              <div className="checkbox-list scrollable">
                {(cvTemplates.city || LITHUANIAN_CITIES.map((c, i) => ({ id: i, category: 'city', value: c })))
                  .filter(city => !locationSearch || city.value.toLowerCase().includes(locationSearch.toLowerCase()))
                  .map((city) => (
                    <label key={city.id || city.value} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={localFilters.location.includes(city.value)}
                        onChange={() => handleLocalArrayCheckbox('location', city.value)}
                      />
                      <span>{city.value}</span>
                    </label>
                  ))}
              </div>
            </div>
          </div>
        </FilterModal>
      )}

      {openModal === 'experience' && (
        <FilterModal
          title="Darbo patirtis"
          icon={<Briefcase size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          countLabel="candidates"
        >
          {/* Tab Navigation */}
          <div className="experience-tabs">
            <button 
              className={`tab-btn ${experienceTab === 'workField' ? 'active' : ''}`}
              onClick={() => setExperienceTab('workField')}
            >
              Darbo patirtis
            </button>
            <button 
              className={`tab-btn ${experienceTab === 'lastWorkField' ? 'active' : ''}`}
              onClick={() => setExperienceTab('lastWorkField')}
            >
              Paskutinė darbo patirtis
            </button>
            <button 
              className={`tab-btn ${experienceTab === 'companies' ? 'active' : ''}`}
              onClick={() => setExperienceTab('companies')}
            >
              Darbo patirtis - įmonės
            </button>
          </div>

          {/* Tab Content */}
          {experienceTab === 'workField' && (
            <div className="two-column-layout">
              {/* Left Column - Work Fields */}
              <div className="filter-column">
                <h4>Darbo sritys</h4>
                <input
                  type="text"
                  placeholder="Įveskite darbo sritį"
                  value={workFieldSearch}
                  onChange={(e) => setWorkFieldSearch(e.target.value)}
                  className="filter-search-input"
                />
                <div className="checkbox-list scrollable">
                  {(cvTemplates.work_field || [])
                    .filter(wf => !workFieldSearch || wf.value.toLowerCase().includes(workFieldSearch.toLowerCase()))
                    .map((workField) => {
                      const count = workFieldCounts.find(w => w.workField === workField.value)?.count || 0;
                      return (
                        <label key={workField.id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={localFilters.experience.includes(workField.value)}
                            onChange={() => handleLocalArrayCheckbox('experience', workField.value)}
                          />
                          <span>{workField.value} <span className="count">({count})</span></span>
                        </label>
                      );
                    })}
                </div>
              </div>
              
              {/* Right Column - Position Types + Positions */}
              <div className="filter-column">
                <h4>Pareigybių tipai</h4>
                <div className="checkbox-list">
                  {(cvTemplates.position_type || []).map((posType) => (
                    <label key={posType.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={localFilters.experience.includes(posType.value)}
                        onChange={() => handleLocalArrayCheckbox('experience', posType.value)}
                      />
                      <span>{posType.value}</span>
                    </label>
                  ))}
                </div>

                <div style={{ marginTop: '24px' }}>
                  <h4>Pareigybės pagal pasirinktas darbo sritis</h4>
                  {localFilters.experience.filter(e => (cvTemplates.work_field || []).some(wf => wf.value === e)).length === 0 ? (
                    <div className="no-results-hint">
                      Pasirinkite bent vieną darbo sritį
                    </div>
                  ) : (
                    <div className="checkbox-list scrollable">
                      {(() => {
                        const selectedWorkFields = localFilters.experience.filter(e =>
                          (cvTemplates.work_field || []).some(wf => wf.value === e)
                        );
                        const filtered = (cvTemplates.position || []).filter(pos =>
                          (pos.parent_value || '').split(',').map(v => v.trim()).some(pf => selectedWorkFields.includes(pf))
                        );
                        if (filtered.length === 0) {
                          return (
                            <div className="no-results-hint">
                              Pasirinktose darbo srityse pareigybių nerasta
                            </div>
                          );
                        }
                        return filtered.map((pos) => (
                          <label key={pos.id} className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={localFilters.experience.includes(pos.value)}
                              onChange={() => handleLocalArrayCheckbox('experience', pos.value)}
                            />
                            <span>{pos.value}</span>
                          </label>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {experienceTab === 'lastWorkField' && (
            <div className="two-column-layout">
              {/* Left Column - Last Work Fields */}
              <div className="filter-column">
                <h4>Darbo sritys</h4>
                <input
                  type="text"
                  placeholder="Įveskite darbo sritį"
                  value={workFieldSearch}
                  onChange={(e) => setWorkFieldSearch(e.target.value)}
                  className="filter-search-input"
                />
                <div className="checkbox-list scrollable two-columns">
                  {(cvTemplates.work_field || [])
                    .filter(wf => !workFieldSearch || wf.value.toLowerCase().includes(workFieldSearch.toLowerCase()))
                    .map((workField) => {
                      const count = lastWorkFieldCounts.find(w => w.workField === workField.value)?.count || 0;
                      return (
                        <label key={workField.id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={(localFilters.lastWorkField || []).includes(workField.value)}
                            onChange={() => handleLocalArrayCheckbox('lastWorkField', workField.value)}
                          />
                          <span>{workField.value} <span className="count">({count})</span></span>
                        </label>
                      );
                    })}
                </div>
              </div>
              
              {/* Right Column - Position Types + Positions */}
              <div className="filter-column">
                <h4>Pareigybių tipai</h4>
                <div className="checkbox-list">
                  {(cvTemplates.position_type || []).map((posType) => (
                    <label key={posType.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={localFilters.experience.includes(posType.value)}
                        onChange={() => handleLocalArrayCheckbox('experience', posType.value)}
                      />
                      <span>{posType.value}</span>
                    </label>
                  ))}
                </div>

                <div style={{ marginTop: '24px' }}>
                  <h4>Pareigybės pagal pasirinktas darbo sritis</h4>
                  {(localFilters.lastWorkField || []).length === 0 ? (
                    <div className="no-results-hint">
                      Pasirinkite bent vieną darbo sritį
                    </div>
                  ) : (
                    <div className="checkbox-list scrollable">
                      {(() => {
                        const filtered = (cvTemplates.position || []).filter(pos =>
                          (pos.parent_value || '').split(',').map(v => v.trim()).some(pf => (localFilters.lastWorkField || []).includes(pf))
                        );
                        if (filtered.length === 0) {
                          return (
                            <div className="no-results-hint">
                              Pasirinktose darbo srityse pareigybių nerasta
                            </div>
                          );
                        }
                        return filtered.map((pos) => (
                          <label key={pos.id} className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={localFilters.experience.includes(pos.value)}
                              onChange={() => handleLocalArrayCheckbox('experience', pos.value)}
                            />
                            <span>{pos.value}</span>
                          </label>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {experienceTab === 'companies' && (
            <div className="two-column-layout">
              {/* Left Column - Company Activity Sectors */}
              <div className="filter-column">
                <h4>Įmonių veiklos sritys</h4>
                <div className="checkbox-list scrollable">
                  {(cvTemplates.work_field || []).map((field) => (
                    <label key={field.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={(localFilters.companyActivity || []).includes(field.value)}
                        onChange={() => handleLocalArrayCheckbox('companyActivity', field.value)}
                      />
                      <span>{field.value}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Right Column - Companies (filtered by selected sectors) */}
              <div className="filter-column companies-column">
                <h4>Įmonės</h4>
                <input
                  type="text"
                  placeholder="Įveskite įmonę"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  className="filter-search-input"
                />
                {loadingCompanies ? (
                  <div className="loading-companies">
                    <Loader2 size={20} className="spin" />
                    <span>Kraunama...</span>
                  </div>
                ) : (
                  <div className="checkbox-list scrollable two-columns">
                    {(() => {
                      const selectedSectors = localFilters.companyActivity || [];
                      const filtered = companies
                        .filter(c => {
                          if (selectedSectors.length > 0 && !selectedSectors.includes(c.activitySector || '')) {
                            return false;
                          }
                          if (companySearch && !c.companyName.toLowerCase().includes(companySearch.toLowerCase())) {
                            return false;
                          }
                          return true;
                        });
                      
                      if (selectedSectors.length > 0 && filtered.length === 0) {
                        return (
                          <div className="no-results-hint">
                            Pasirinktose veiklos srityse įmonių nerasta
                          </div>
                        );
                      }
                      
                      return filtered.map((company, idx) => (
                        <label key={idx} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={(localFilters.companyNames || []).includes(company.companyName)}
                            onChange={() => handleLocalArrayCheckbox('companyNames', company.companyName)}
                          />
                          <span>{company.companyName} <span className="count">({company.count})</span></span>
                        </label>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </FilterModal>
      )}

      {openModal === 'education' && (
        <FilterModal
          title="Išsilavinimas"
          icon={<GraduationCap size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          countLabel="candidates"
        >
          <div className="modal-filter-group">
            <div className="checkbox-list">
              {(cvTemplates.education_level || []).map((edu) => (
                <label key={edu.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={localFilters.education.includes(edu.value)}
                    onChange={() => handleLocalArrayCheckbox('education', edu.value)}
                  />
                  <span>{edu.value}</span>
                </label>
              ))}
            </div>
          </div>
        </FilterModal>
      )}

      {openModal === 'languages' && (
        <FilterModal
          title="Kalbų įgūdžiai"
          icon={<Globe size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          countLabel="candidates"
        >
          <div className="modal-filter-group">
            <div className="checkbox-list">
              {(cvTemplates.language || []).map((lang) => (
                <label key={lang.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={localFilters.languages.includes(lang.value)}
                    onChange={() => handleLocalArrayCheckbox('languages', lang.value)}
                  />
                  <span>{lang.value}</span>
                </label>
              ))}
            </div>
          </div>
        </FilterModal>
      )}

      {openModal === 'computerSkills' && (
        <FilterModal
          title="Darbo kompiuteriu įgūdžiai"
          icon={<Monitor size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          countLabel="candidates"
        >
          {/* Office Programs */}
          {(cvTemplates.computer_skill_office || []).length > 0 && (
            <div className="modal-filter-group">
              <label>Biuro programos</label>
              <div className="checkbox-list">
                {cvTemplates.computer_skill_office!.map((skill) => (
                  <label key={skill.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.computerSkills.includes(skill.value)}
                      onChange={() => handleLocalArrayCheckbox('computerSkills', skill.value)}
                    />
                    <span>{skill.value}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Accounting Programs */}
          {(cvTemplates.computer_skill_accounting || []).length > 0 && (
            <div className="modal-filter-group">
              <label>Apskaitos programos</label>
              <div className="checkbox-list">
                {cvTemplates.computer_skill_accounting!.map((skill) => (
                  <label key={skill.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.computerSkills.includes(skill.value)}
                      onChange={() => handleLocalArrayCheckbox('computerSkills', skill.value)}
                    />
                    <span>{skill.value}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Operating Systems */}
          {(cvTemplates.computer_skill_os || []).length > 0 && (
            <div className="modal-filter-group">
              <label>Operacinės sistemos</label>
              <div className="checkbox-list">
                {cvTemplates.computer_skill_os!.map((skill) => (
                  <label key={skill.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.computerSkills.includes(skill.value)}
                      onChange={() => handleLocalArrayCheckbox('computerSkills', skill.value)}
                    />
                    <span>{skill.value}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Programming Languages */}
          {(cvTemplates.computer_skill_programming || []).length > 0 && (
            <div className="modal-filter-group">
              <label>Programavimo kalbos</label>
              <div className="checkbox-list">
                {cvTemplates.computer_skill_programming!.map((skill) => (
                  <label key={skill.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.computerSkills.includes(skill.value)}
                      onChange={() => handleLocalArrayCheckbox('computerSkills', skill.value)}
                    />
                    <span>{skill.value}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Databases */}
          {(cvTemplates.computer_skill_database || []).length > 0 && (
            <div className="modal-filter-group">
              <label>Duomenų bazės</label>
              <div className="checkbox-list">
                {cvTemplates.computer_skill_database!.map((skill) => (
                  <label key={skill.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.computerSkills.includes(skill.value)}
                      onChange={() => handleLocalArrayCheckbox('computerSkills', skill.value)}
                    />
                    <span>{skill.value}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </FilterModal>
      )}

      {openModal === 'drivingSkills' && (
        <FilterModal
          title="Vairavimo įgūdžiai"
          icon={<Car size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          countLabel="candidates"
        >
          <div className="modal-filter-group">
            <div className="checkbox-list">
              {(cvTemplates.driving_category || []).map((license) => (
                <label key={license.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={localFilters.drivingLicense.includes(license.value)}
                    onChange={() => handleLocalArrayCheckbox('drivingLicense', license.value)}
                  />
                  <span>{license.value}</span>
                </label>
              ))}
            </div>
          </div>
        </FilterModal>
      )}

      {openModal === 'requirements' && (
        <FilterModal
          title="Reikalavimai būsimam darbuotojui"
          icon={<Target size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          countLabel="candidates"
        >
          <div className="modal-filter-group">
            <label>Darbo tipas</label>
            <div className="checkbox-list">
              {(cvTemplates.work_type || []).map((type) => (
                <label key={type.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={localFilters.workType.includes(type.value)}
                    onChange={() => handleLocalArrayCheckbox('workType', type.value)}
                  />
                  <span>{type.value}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="modal-filter-group">
            <label>Pageidaujamas atlyginimas (nuo)</label>
            <input
              type="text"
              placeholder="Pvz.: 1000"
              value={localFilters.salary}
              onChange={(e) => handleLocalInputChange('salary', e.target.value)}
            />
          </div>
        </FilterModal>
      )}

      {/* Save Search Modal */}
      {showSaveModal && (
        <div className="custom-modal-overlay" onClick={cancelSaveSearch}>
          <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>Išsaugoti paiešką</h3>
              <button className="custom-modal-close" onClick={cancelSaveSearch}>
                <X size={24} />
              </button>
            </div>
            <div className="custom-modal-body">
              <p>Ar tikrai norite išsaugoti šią paiešką?</p>
              <div className="modal-filter-group">
                <label>Paieškos pavadinimas *</label>
                <input
                  type="text"
                  placeholder="Pvz.: Programuotojai Vilniuje"
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  disabled={isSaving}
                  autoFocus
                />
              </div>
              {saveMessage && (
                <div className={`save-message ${saveMessage.type}`}>
                  {saveMessage.text}
                </div>
              )}
            </div>
            <div className="custom-modal-footer">
              <button 
                className="btn-secondary" 
                onClick={cancelSaveSearch}
                disabled={isSaving}
              >
                Atšaukti
              </button>
              <button 
                className="btn-primary" 
                onClick={confirmSaveSearch}
                disabled={isSaving}
              >
                {isSaving ? 'Saugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateSearchFilters;

